import { Box } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { planDisplayName } from '../../common/planDisplayName';
import { useCompanyLogo } from '../../hooks/useCompanyLogo';
import { usePermissions } from '../../hooks/usePermissions';
import { KitPill } from '../common/MobileKit';
import { ProtoIcon } from '../common/ProtoIcon';

function initialsOf(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
}

/**
 * Identidade exibida no header da aba "Mais" (no lugar do título "Mais"):
 * logo do tenant, nome do usuário logado, nome da empresa e plano.
 * Para o owner, toca e abre Minha conta (mesmo comportamento do antigo cartão do usuário).
 */
export default function MobileMoreHeader() {
    const navigate = useNavigate();
    const { userData } = usePermissions();
    const { companyLogo } = useCompanyLogo();
    const [companyName, setCompanyName] = React.useState('');
    const [planName, setPlanName] = React.useState('');

    // Empresa e plano (endpoints já usados em Minha Conta; liberados a qualquer usuário autenticado).
    React.useEffect(() => {
        let active = true;
        httpClient.get('/enterprise/account-info')
            .then((response) => {
                if (active && response.status === 200) setCompanyName(response.data?.trade_name || response.data?.legal_name || '');
            })
            .catch(() => {});
        httpClient.get('/enterprise/plan-usage')
            .then((response) => {
                if (active && response.status === 200 && response.data?.has_plan) setPlanName(response.data.plan_name || '');
            })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    const userName = userData ? (userData.name || userData.username || '') : '';
    const isOwner = userData?.is_owner === true;

    return (
        <Box
            component={isOwner ? 'button' : 'div'}
            type={isOwner ? 'button' : undefined}
            onClick={isOwner ? () => navigate('/myaccount') : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                p: '2px 4px 6px',
                m: 0,
                border: 0,
                background: 'none',
                textAlign: 'left',
                fontFamily: 'inherit',
                color: 'text.primary',
                cursor: isOwner ? 'pointer' : 'default',
            }}
        >
            <Box
                component="span"
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    backgroundImage: companyLogo ? 'none' : (t) => t.palette.gradient.brand,
                    backgroundColor: companyLogo ? 'background.default' : undefined,
                    border: companyLogo ? '1px solid' : 0,
                    borderColor: 'divider',
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 20,
                    flexShrink: 0,
                    overflow: 'hidden',
                }}
            >
                {companyLogo
                    ? <img src={companyLogo} alt="Logo da empresa" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : initialsOf(companyName || userName)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>
                <Box sx={{ fontWeight: 800, fontSize: 18, letterSpacing: '-.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userName || 'Usuário'}
                </Box>
                {companyName && (
                    <Box sx={{ fontSize: 12.5, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {companyName}
                    </Box>
                )}
                {planName && (
                    <Box sx={{ mt: '4px' }}>
                        <KitPill tone="info">Plano {planDisplayName(planName)}</KitPill>
                    </Box>
                )}
            </Box>
            {isOwner && <Box sx={{ display: 'inline-flex', color: 'text.primary' }}><ProtoIcon name="right" size={18} /></Box>}
        </Box>
    );
}
